import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    Motorcycle, MapPin, Phone, Clock, Check,
    Person, Package, ArrowsClockwise, Power, Printer,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { api, authHeaders } from "@/lib/api";
import PageHeader from "@/admin/components/PageHeader";
import { smartPrint } from "@/lib/smartPrint";

const POLL_MS = 8_000;

// Compact relative time — "5m" / "1h12m". Used everywhere on the board so the
// dispatcher can prioritize the oldest orders at a glance.
function ago(iso) {
    if (!iso) return "";
    const diff = Math.max(0, Date.now() - new Date(iso).getTime());
    const min = Math.floor(diff / 60_000);
    if (min < 1) return "just now";
    if (min < 60) return `${min}m`;
    const h = Math.floor(min / 60);
    return `${h}h${min % 60 ? `${min % 60}m` : ""}`;
}

export default function Delivery() {
    const [board, setBoard] = useState({ unassigned: [], in_route: [], delivered: [] });
    const [drivers, setDrivers] = useState([]);
    const [deliveryEnabled, setDeliveryEnabled] = useState(true);
    const [assigning, setAssigning] = useState(null); // order being assigned
    const [eta, setEta] = useState(20);
    const [pickedDriver, setPickedDriver] = useState("");
    const [busy, setBusy] = useState(false);

    const fetchAll = useCallback(async () => {
        try {
            const [b, d, s] = await Promise.all([
                api.get("/admin/delivery/board", { headers: authHeaders() }),
                api.get("/admin/drivers", { headers: authHeaders() }),
                api.get("/settings"),
            ]);
            setBoard(b.data);
            setDrivers(d.data.drivers || []);
            setDeliveryEnabled(s.data.delivery_enabled !== false);
        } catch {
            // ignore — show what we have
        }
    }, []);

    useEffect(() => {
        fetchAll();
        const id = setInterval(fetchAll, POLL_MS);
        return () => clearInterval(id);
    }, [fetchAll]);

    const toggleDelivery = async (v) => {
        setDeliveryEnabled(v);
        try {
            await api.put(
                "/admin/settings/delivery",
                { enabled: v },
                { headers: authHeaders() },
            );
            toast.success(v ? "Delivery activado" : "Delivery pausado");
        } catch (e) {
            setDeliveryEnabled(!v);
            toast.error(e?.response?.data?.detail || "No se pudo cambiar");
        }
    };

    const openAssign = (order) => {
        setAssigning(order);
        setPickedDriver("");
        setEta(20);
    };

    const confirmAssign = async () => {
        if (!pickedDriver) {
            toast.error("Selecciona un repartidor");
            return;
        }
        setBusy(true);
        try {
            await api.put(
                `/admin/orders/${assigning.id}/assign-driver`,
                { driver_id: pickedDriver, eta_minutes: Number(eta) || 20 },
                { headers: authHeaders() },
            );
            toast.success("Repartidor asignado");
            setAssigning(null);
            fetchAll();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo asignar");
        } finally {
            setBusy(false);
        }
    };

    const unassign = async (order) => {
        if (!window.confirm(`¿Quitar el repartidor del pedido #${order.id.slice(-6).toUpperCase()}?`)) return;
        try {
            await api.delete(`/admin/orders/${order.id}/assign-driver`, { headers: authHeaders() });
            toast.success("Repartidor removido");
            fetchAll();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error");
        }
    };

    const updateEta = async (order, minutes) => {
        try {
            await api.put(
                `/admin/orders/${order.id}/eta`,
                { eta_minutes: minutes },
                { headers: authHeaders() },
            );
            toast.success(`ETA actualizada a ${minutes} min`);
            fetchAll();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error");
        }
    };

    const counts = useMemo(() => ({
        unassigned: board.unassigned.length,
        in_route: board.in_route.length,
        delivered: board.delivered.length,
    }), [board]);

    return (
        <div data-testid="admin-delivery">
            <PageHeader
                kicker="Dispatch"
                title="Delivery / Repartos"
                action={
                    <Button
                        variant="outline"
                        onClick={fetchAll}
                        className="rounded-full"
                        data-testid="delivery-refresh"
                    >
                        <ArrowsClockwise size={14} weight="bold" className="mr-1.5" /> Refrescar
                    </Button>
                }
            />

            {/* Master toggle card */}
            <div
                data-testid="delivery-master-toggle"
                className={`rounded-3xl border p-5 mb-6 flex items-center gap-4 transition-colors ${
                    deliveryEnabled
                        ? "bg-blue-50 border-blue-200"
                        : "bg-amber-50 border-amber-200"
                }`}
            >
                <span
                    className={`grid place-items-center w-14 h-14 rounded-full ${
                        deliveryEnabled ? "bg-blue-200 text-blue-900" : "bg-amber-200 text-amber-900"
                    }`}
                >
                    <Power size={26} weight="fill" />
                </span>
                <div className="flex-1">
                    <p className="font-display font-black text-2xl">
                        {deliveryEnabled ? "Delivery ACTIVO" : "Delivery PAUSADO"}
                    </p>
                    <p className="text-sm text-muted-foreground">
                        {deliveryEnabled
                            ? "Los clientes pueden ordenar a domicilio."
                            : "Solo pickup. Los pedidos a domicilio están bloqueados."}
                    </p>
                </div>
                <Switch
                    data-testid="delivery-toggle"
                    checked={deliveryEnabled}
                    onCheckedChange={toggleDelivery}
                    className="scale-150 data-[state=checked]:bg-blue-500"
                />
            </div>

            {/* Drivers row */}
            <div className="mb-6">
                <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2">
                    Repartidores ({drivers.length})
                </p>
                <div className="flex gap-2 overflow-x-auto pb-2">
                    {drivers.map((d) => (
                        <div
                            key={d.id}
                            data-testid={`driver-card-${d.id}`}
                            className="shrink-0 bg-white border border-border rounded-2xl px-3 py-2 flex items-center gap-2"
                        >
                            <span className="grid place-items-center h-8 w-8 rounded-full bg-foreground text-background">
                                <Motorcycle size={14} weight="bold" />
                            </span>
                            <div>
                                <p className="font-bold text-sm leading-none">{d.name}</p>
                                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
                                    {d.active_orders} activos
                                </p>
                            </div>
                        </div>
                    ))}
                    {drivers.length === 0 && (
                        <p className="text-xs text-muted-foreground italic">
                            No hay repartidores. Crea un usuario staff con módulo &ldquo;Delivery&rdquo; en Users.
                        </p>
                    )}
                </div>
            </div>

            {/* Kanban */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <Column
                    title="Esperando asignar"
                    count={counts.unassigned}
                    accent="bg-amber-100 text-amber-900"
                    testId="col-unassigned"
                    empty="Sin pedidos esperando."
                >
                    {board.unassigned.map((o) => (
                        <OrderCard key={o.id} order={o} testIdPrefix="unassigned" tone="amber">
                            <Button
                                size="sm"
                                onClick={() => openAssign(o)}
                                data-testid={`assign-${o.id}`}
                                className="w-full rounded-full bg-primary hover:bg-primary/90 font-bold uppercase tracking-widest"
                            >
                                <Motorcycle size={14} weight="bold" className="mr-1.5" />
                                Asignar repartidor
                            </Button>
                        </OrderCard>
                    ))}
                </Column>

                <Column
                    title="En ruta"
                    count={counts.in_route}
                    accent="bg-blue-100 text-blue-900"
                    testId="col-in-route"
                    empty="Sin entregas activas."
                >
                    {board.in_route.map((o) => (
                        <OrderCard key={o.id} order={o} testIdPrefix="in-route" tone="blue">
                            <div className="flex items-center justify-between text-xs">
                                <span className="inline-flex items-center gap-1">
                                    <Person size={12} weight="bold" />
                                    <span className="font-bold">{o.driver_name}</span>
                                </span>
                                <button
                                    onClick={() => unassign(o)}
                                    className="text-destructive hover:underline text-[11px] font-bold uppercase tracking-widest"
                                    data-testid={`unassign-${o.id}`}
                                >
                                    Quitar
                                </button>
                            </div>
                            <EtaControl
                                eta={o.eta_minutes || 20}
                                pickedUp={!!o.picked_up_at}
                                onUpdate={(m) => updateEta(o, m)}
                            />
                        </OrderCard>
                    ))}
                </Column>

                <Column
                    title="Entregados (hoy)"
                    count={counts.delivered}
                    accent="bg-emerald-100 text-emerald-900"
                    testId="col-delivered"
                    empty="Aún no hay entregas completadas."
                >
                    {board.delivered.map((o) => (
                        <OrderCard key={o.id} order={o} testIdPrefix="delivered" tone="emerald">
                            <div className="flex items-center justify-between text-xs text-muted-foreground">
                                <span className="inline-flex items-center gap-1">
                                    <Check size={12} weight="bold" /> {o.driver_name || "—"}
                                </span>
                                <span>{ago(o.delivered_at)} ago</span>
                            </div>
                        </OrderCard>
                    ))}
                </Column>
            </div>

            {/* Assign dialog */}
            <Dialog open={!!assigning} onOpenChange={(o) => !o && setAssigning(null)}>
                <DialogContent data-testid="assign-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display font-black text-2xl">
                            Asignar repartidor
                        </DialogTitle>
                    </DialogHeader>
                    {assigning && (
                        <div className="space-y-3">
                            <div className="rounded-2xl bg-muted/50 p-3 text-sm">
                                <p className="font-bold">{assigning.customer_name}</p>
                                <p className="text-muted-foreground text-xs">
                                    {assigning.address || "Sin dirección"}
                                </p>
                                <p className="text-[11px] text-muted-foreground mt-1">
                                    Pedido #{assigning.id.slice(-6).toUpperCase()} · ${Number(assigning.total).toFixed(2)}
                                </p>
                            </div>

                            <div>
                                <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold block mb-1">
                                    Repartidor
                                </label>
                                <select
                                    data-testid="assign-driver-select"
                                    value={pickedDriver}
                                    onChange={(e) => setPickedDriver(e.target.value)}
                                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                                >
                                    <option value="">— Selecciona —</option>
                                    {drivers.map((d) => (
                                        <option key={d.id} value={d.id}>
                                            {d.name} ({d.active_orders} activos)
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold block mb-1">
                                    ETA en minutos
                                </label>
                                <Input
                                    data-testid="assign-eta"
                                    type="number"
                                    min="5"
                                    max="120"
                                    value={eta}
                                    onChange={(e) => setEta(e.target.value)}
                                />
                                <p className="text-[10px] text-muted-foreground mt-1">
                                    Tiempo estimado para que llegue al cliente.
                                </p>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setAssigning(null)} className="rounded-full">
                            Cancelar
                        </Button>
                        <Button
                            onClick={confirmAssign}
                            disabled={busy}
                            data-testid="assign-confirm"
                            className="rounded-full bg-primary hover:bg-primary/90 font-bold uppercase tracking-widest"
                        >
                            {busy ? "..." : "Asignar"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Column({ title, count, accent, testId, empty, children }) {
    return (
        <section
            data-testid={testId}
            className="bg-white rounded-3xl border border-border overflow-hidden flex flex-col min-h-[28vh] lg:min-h-[60vh]"
        >
            <header className={`px-4 py-3 flex items-center justify-between ${accent}`}>
                <h3 className="font-display font-black tracking-tight">{title}</h3>
                <span className="text-xs font-bold uppercase tracking-widest bg-white/70 rounded-full px-2 py-0.5">
                    {count}
                </span>
            </header>
            <div className="p-3 flex-1 space-y-3 overflow-y-auto">
                {count === 0 ? (
                    <p className="text-center text-xs text-muted-foreground italic py-8">{empty}</p>
                ) : children}
            </div>
        </section>
    );
}

function OrderCard({ order, tone = "amber", testIdPrefix, children }) {
    const idShort = (order.id || "").slice(-6).toUpperCase();
    const phoneLink = order.customer_phone ? `tel:${order.customer_phone.replace(/[^+0-9]/g, "")}` : null;
    const mapsLink = order.address
        ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(order.address)}`
        : null;
    return (
        <article
            data-testid={`${testIdPrefix}-order-${order.id}`}
            className={`rounded-2xl border-2 p-3 space-y-2 bg-${tone}-50/30`}
            style={{ borderColor: `var(--card-border-${tone}, transparent)` }}
        >
            <div className="flex items-start justify-between">
                <div>
                    <p className="font-bold text-sm leading-tight">{order.customer_name}</p>
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
                        #{idShort} · ${Number(order.total).toFixed(2)}
                    </p>
                </div>
                <span className="text-[10px] inline-flex items-center gap-1 text-muted-foreground">
                    <Clock size={10} weight="bold" /> {ago(order.created_at)}
                </span>
            </div>
            {order.address && (
                <p className="text-xs text-foreground/80 inline-flex items-start gap-1">
                    <MapPin size={12} weight="fill" className="text-primary mt-0.5 shrink-0" />
                    <span className="line-clamp-2">{order.address}</span>
                </p>
            )}
            <div className="flex flex-wrap gap-1">
                {(order.items || []).slice(0, 3).map((it, i) => (
                    <span key={i} className="text-[10px] bg-white border border-border rounded-full px-2 py-0.5">
                        {it.qty}× <span className="font-bold">{it.name}</span>
                    </span>
                ))}
                {(order.items || []).length > 3 && (
                    <span className="text-[10px] text-muted-foreground">
                        +{order.items.length - 3} más
                    </span>
                )}
            </div>
            <div className="flex gap-2">
                {phoneLink && (
                    <a
                        href={phoneLink}
                        data-testid={`call-${order.id}`}
                        className="flex-1 inline-flex items-center justify-center gap-1 rounded-full bg-white border border-border text-xs font-bold uppercase tracking-widest py-1.5 hover:bg-muted"
                    >
                        <Phone size={12} weight="fill" /> Llamar
                    </a>
                )}
                {mapsLink && (
                    <a
                        href={mapsLink}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`map-${order.id}`}
                        className="flex-1 inline-flex items-center justify-center gap-1 rounded-full bg-white border border-border text-xs font-bold uppercase tracking-widest py-1.5 hover:bg-muted"
                    >
                        <MapPin size={12} weight="fill" /> Mapa
                    </a>
                )}
                <button
                    onClick={() => smartPrint(order, { kind: "customer" })}
                    data-testid={`print-${order.id}`}
                    className="inline-flex items-center justify-center gap-1 rounded-full bg-white border border-border text-xs font-bold uppercase tracking-widest py-1.5 px-3 hover:bg-muted"
                    title="Imprimir recibo"
                >
                    <Printer size={12} weight="fill" />
                </button>
            </div>
            {children}
        </article>
    );
}

// EtaControl: lets dispatch edit the ETA and shows "recogido" pill once the
// driver has picked the order up at the deli.
function EtaControl({ eta, pickedUp, onUpdate }) {
    const [value, setValue] = useState(eta);
    useEffect(() => setValue(eta), [eta]);
    return (
        <div className="flex items-center gap-2 bg-white border border-border rounded-full px-2 py-1">
            <Clock size={12} weight="bold" className="text-primary" />
            <span className="text-[10px] uppercase tracking-widest font-bold">ETA</span>
            <input
                type="number"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                min="5"
                max="120"
                className="w-14 text-sm font-bold bg-transparent outline-none"
            />
            <span className="text-[10px] text-muted-foreground">min</span>
            <button
                onClick={() => onUpdate(Number(value) || 20)}
                className="text-[10px] uppercase tracking-widest font-bold text-primary hover:underline"
            >
                Guardar
            </button>
            {pickedUp && (
                <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-blue-700">
                    <Package size={10} weight="fill" /> recogido
                </span>
            )}
        </div>
    );
}
